"use client";

import { createContext, useContext, useCallback, ReactNode } from "react";
import { useRouter } from "next/navigation";
import { reportWarning } from "@/lib/observability/report-error";

interface AuthContextType {
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const router = useRouter();

  const signOut = useCallback(async () => {
    // Call server-side signout API to invalidate Redis cache + Supabase session
    try {
      const res = await fetch("/api/auth/signout/", {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) {
        console.error("Sign-out failed:", res.status);
      }
    } catch (err) {
      console.error("Sign-out error:", err);
      reportWarning(err, {
        source: "contexts.AuthContext",
        feature: "auth",
        action: "signOut",
      });
    }
    // Always navigate to login — even on failure, clear client state
    router.push("/login");
  }, [router]);

  return (
    <AuthContext.Provider value={{ signOut }}>{children}</AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
